import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import { formatDate, getRelatedTopics, getTopicArticles, getTopicById } from "@/utils/content";

export default function TopicPage() {
  const { topicId = "" } = useParams();
  const topic = getTopicById(topicId);

  if (!topic) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-6">
        <Seo title="专题不存在 - 噜噜社" description="未找到对应的专题内容。" />
        <i className="ri-bookmark-3-line text-5xl text-foreground-300" />
        <h1 className="mt-4 text-2xl font-bold text-foreground-950">专题不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">该专题可能已下线，去首页看看其他内容吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const list = getTopicArticles(topic);
  const relatedTopics = getRelatedTopics(topic);

  return (
    <>
      <Seo
        title={`${topic.title} - 噜噜社专题`}
        description={topic.intro.slice(0, 120)}
        keywords={`${topic.title},噜噜社专题,娱乐资讯,潮流文化`}
      />

      {/* 主视觉 */}
      <section className="relative h-64 w-full overflow-hidden md:h-[440px]">
        <img src={topic.cover} alt={topic.title} title={`${topic.title} - 噜噜社专题`} className="h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/90 via-secondary-950/50 to-secondary-950/25" />
        <div className="absolute inset-0 mx-auto flex w-full max-w-[1280px] flex-col justify-end px-4 pb-10 md:px-6">
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-bookmark-3-fill" />
            专 题
          </span>
          <h1 className="mt-4 text-3xl font-extrabold text-background-50 md:text-5xl">{topic.title}</h1>
          <p className="mt-2 text-base font-medium text-primary-300 md:text-lg">{topic.subtitle}</p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span>专题</span>
          <i className="ri-arrow-right-s-line" />
          <span className="font-medium text-foreground-900">{topic.title}</span>
        </nav>

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0">
            {/* 导语 */}
            <div className="rounded-lg border-l-4 border-accent-500 bg-accent-50/70 p-5">
              <p className="flex items-center gap-2 text-sm font-semibold text-accent-800">
                <i className="ri-quote-text" />
                专题导语
              </p>
              <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">{topic.intro}</p>
            </div>

            {/* 内容集合 */}
            <div className="mt-8 flex items-center gap-2">
              <span className="h-5 w-1 rounded-full bg-primary-500" />
              <h2 className="text-xl font-bold text-foreground-950">专题内容（{list.length}）</h2>
            </div>
            <div className="mt-4 flex flex-col gap-4">
              {list.map((a, i) => (
                <div key={a.id} className="relative">
                  <span className="absolute -left-1 top-4 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-background-50">
                    {i + 1}
                  </span>
                  <div className="pl-4">
                    <ArticleCard article={a} variant="list" />
                  </div>
                </div>
              ))}
            </div>

            {/* 编辑点评 */}
            <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-award-line text-primary-500" />
                编辑点评
              </p>
              <p className="mt-3 text-sm leading-loose text-foreground-700">{topic.review}</p>
            </div>
          </div>

          {/* 侧栏：相关专题 */}
          <aside className="flex flex-col gap-4">
            <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <i className="ri-links-line text-accent-500" />
              相关专题
            </h3>
            {relatedTopics.map((t) => (
              <Link
                key={t.id}
                to={`/topic/${t.id}`}
                className="group flex cursor-pointer gap-3 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-accent-300"
              >
                <div className="h-20 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={t.cover}
                    alt={t.title}
                    title={`${t.title} - 噜噜社专题`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-sm font-semibold text-foreground-900 transition-colors group-hover:text-accent-600">
                    {t.title}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">{t.subtitle}</p>
                  <p className="mt-2 text-xs text-foreground-400">{formatDate(t.date)}</p>
                </div>
              </Link>
            ))}
          </aside>
        </div>
      </div>
    </>
  );
}